import { useState, useEffect, useCallback } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { auth } from '../config/firebase';
import { loginSuccess, logout as reduxLogout, initializationComplete } from '../redux/authSlice';

// Simple, clear auth states
type AuthState = 'loading' | 'unauthenticated' | 'authenticated' | 'ready';

// User flow types for smart UX decisions
type UserFlow = 'new_user' | 'returning_user' | 'trial_user' | 'subscriber' | 'guest';

interface SimplifiedUser {
  uid: string;
  email: string;
  displayName: string;
  isNewUser: boolean;
  subscription: {
    status: 'none' | 'trial' | 'active' | 'past_due' | 'canceled';
    plan?: 'monthly' | 'yearly';
    hasStarlink?: boolean;
  };
  setup: {
    locationComplete: boolean;
    onboardingComplete: boolean;
    completionPercentage: number;
  };
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
    city?: string;
    state?: string;
    country?: string;
    timestamp: string;
  };
}

interface UseSimplifiedAuthReturn {
  // Core state
  user: SimplifiedUser | null;
  firebaseUser: FirebaseUser | null;
  authState: AuthState;
  userFlow: UserFlow;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile?: (data: any) => Promise<void>;
  
  // Navigation helpers
  requiresAuth: (callback: () => void) => boolean;
  requiresSubscription: (callback: () => void) => boolean;
  
  // Smart shortcuts
  showOneClickCheckout: () => void;
  goToDashboard: () => void;
}

export const useSimplifiedAuth = (): UseSimplifiedAuthReturn => {
  const [user, setUser] = useState<SimplifiedUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Determine user flow based on state
  const getUserFlow = (user: SimplifiedUser | null): UserFlow => {
    if (!user) return 'guest';
    if (user.isNewUser) return 'new_user';
    if (user.subscription.status === 'trial') return 'trial_user';
    if (user.subscription.status === 'active') return 'subscriber';
    return 'returning_user';
  };

  const userFlow = getUserFlow(user);
  const isLoading = authState === 'loading';

  // Fetch user data from backend
  const fetchUserData = async (firebaseUser: FirebaseUser): Promise<SimplifiedUser> => {
    const idToken = await firebaseUser.getIdToken(true);
    const response = await fetch('/api/auth/firebase', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        idToken,
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0]
      })
    });

    if (!response.ok) {
      throw new Error('Failed to authenticate with backend');
    }

    const userData = await response.json();
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email!,
      displayName: firebaseUser.displayName || userData.name || 'Space Explorer',
      isNewUser: userData.isNewUser || false,
      subscription: {
        status: userData.subscription?.status || 'none',
        plan: userData.subscription?.plan,
        hasStarlink: userData.subscription?.addons?.includes('starlink') || false
      },
      setup: {
        locationComplete: !!userData.location,
        onboardingComplete: userData.onboarding?.completed || false,
        completionPercentage: calculateCompletionPercentage(userData)
      },
      location: userData.location
    };
  };

  const calculateCompletionPercentage = (userData: any): number => {
    let completed = 0;
    const total = 4;
    
    if (userData.location) completed++;
    if (userData.preferences) completed++;
    if (userData.subscription?.status === 'active') completed++;
    if (userData.onboarding?.completed) completed++;
    
    return Math.round((completed / total) * 100);
  };

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentFirebaseUser) => {
      try {
        if (currentFirebaseUser) {
          console.log('🔐 SimplifiedAuth: User authenticated:', currentFirebaseUser.email);
          setFirebaseUser(currentFirebaseUser);
          
          try {
            const userData = await fetchUserData(currentFirebaseUser);
            setUser(userData);
            setAuthState('authenticated');
            setError(null);
            
            // Sync with Redux store
            const idToken = await currentFirebaseUser.getIdToken(true);
            dispatch(loginSuccess({
              token: idToken,
              email: currentFirebaseUser.email!,
              hasCompletedOnboarding: userData.setup.onboardingComplete,
              user: {
                id: userData.uid,
                email: userData.email,
                tier: userData.subscription.status === 'active' ? 'pro' : 'free',
                isAdmin: false, // TODO: check admin status from backend
                profile: userData
              }
            }));
          } catch (apiError) {
            console.warn('⚠️ SimplifiedAuth: API error, using fallback user data:', apiError);
            // Create fallback user data if API fails
            const fallbackUser: SimplifiedUser = {
              uid: currentFirebaseUser.uid,
              email: currentFirebaseUser.email!,
              displayName: currentFirebaseUser.displayName || 'Space Explorer',
              isNewUser: false,
              subscription: {
                status: 'active', // Fallback to active for existing user
                plan: 'monthly',
                hasStarlink: false
              },
              setup: {
                locationComplete: false,
                onboardingComplete: true,
                completionPercentage: 75
              }
            };
            setUser(fallbackUser);
            setAuthState('authenticated');
            setError('API temporarily unavailable - using cached data');
            
            // Sync fallback data with Redux store
            const idToken = await currentFirebaseUser.getIdToken(true);
            dispatch(loginSuccess({
              token: idToken,
              email: currentFirebaseUser.email!,
              hasCompletedOnboarding: true,
              user: {
                id: fallbackUser.uid,
                email: fallbackUser.email,
                tier: 'pro', // Fallback for existing user
                isAdmin: false,
                profile: fallbackUser
              }
            }));
          }
        } else {
          console.log('👻 SimplifiedAuth: No user found');
          setFirebaseUser(null);
          setUser(null);
          setAuthState('unauthenticated');
          setError(null);
          
          // Sync logout with Redux store
          dispatch(reduxLogout());
        }
      } catch (error) {
        console.error('🚨 SimplifiedAuth: Error:', error);
        setError(error instanceof Error ? error.message : 'Authentication failed');
        setAuthState('unauthenticated');
        setFirebaseUser(null);
        setUser(null);
      }
      
      // Mark initialization as complete
      dispatch(initializationComplete());
    });

    return unsubscribe;
  }, [dispatch]);

  // Google sign-in
  const signInWithGoogle = useCallback(async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      await signInWithPopup(auth, provider);
      // Auth state change will be handled by the listener
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      if (error.code === 'auth/popup-blocked') {
        setError('Pop-up was blocked. Please allow pop-ups and try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.');
      } else {
        setError('Google sign-in failed. Please try again.');
      }
      throw error;
    }
  }, []);

  // Sign out
  const signOutUser = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setAuthState('unauthenticated');
      setError(null);
      
      // Sync logout with Redux store
      dispatch(reduxLogout());
      
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
      setError('Failed to sign out');
    }
  }, [navigate, dispatch]);

  // Navigation helpers that prevent redirect loops
  const requiresAuth = useCallback((callback: () => void): boolean => {
    if (authState === 'authenticated') {
      callback();
      return true;
    } else if (authState === 'unauthenticated') {
      // Show auth modal instead of redirecting
      showOneClickCheckout();
      return false;
    }
    // Still loading, don't do anything
    return false;
  }, [authState]);

  const requiresSubscription = useCallback((callback: () => void): boolean => {
    if (!user) {
      showOneClickCheckout();
      return false;
    }
    
    if (user.subscription.status === 'active' || user.subscription.status === 'trial') {
      callback();
      return true;
    } else {
      // Show upgrade modal
      showOneClickCheckout();
      return false;
    }
  }, [user]);

  // Smart shortcuts
  const showOneClickCheckout = useCallback(() => {
    // Trigger modal state (handled by parent component)
    const event = new CustomEvent('showOneClickCheckout', { 
      detail: { userFlow, currentUser: user } 
    });
    window.dispatchEvent(event);
  }, [userFlow, user]);

  const goToDashboard = useCallback(() => {
    if (authState === 'authenticated') {
      navigate('/dashboard', { replace: true });
    } else {
      showOneClickCheckout();
    }
  }, [authState, navigate, showOneClickCheckout]);

  // Update user profile (simplified implementation)
  const updateUserProfile = useCallback(async (data: any) => {
    if (!firebaseUser) {
      throw new Error('User not authenticated');
    }
    
    try {
      const idToken = await firebaseUser.getIdToken(true);
      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      // Refresh user data
      const userData = await fetchUserData(firebaseUser);
      setUser(userData);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }, [firebaseUser]);

  return {
    user,
    firebaseUser,
    authState,
    userFlow,
    isLoading,
    error,
    signInWithGoogle,
    signOut: signOutUser,
    updateUserProfile,
    requiresAuth,
    requiresSubscription,
    showOneClickCheckout,
    goToDashboard
  };
};